/**
 * Nom d'affichage d'un centre (fiche « établissement » portée par une ligne
 * Physician sans titre) — DISPLAY ONLY, ne touche ni aux slugs ni à la base.
 *
 * POURQUOI : 295 des 1 957 centres portent une raison sociale bourrée de
 * mots-clés par la source (« Centre Dentaire Oxford Casablanca - Orthodontiste
 * Casablanca I Implant dentaire I Facette dentaire I Dentiste Casablanca »,
 * « Dentiste Casablanca - Master Smile Clinic »). Ces segments « <spé> <ville> »
 * finissent dans le <title>, le H1 et le JSON-LD : sur « dentiste rabat »
 * (90 j), 54 % de nos impressions vont à ces fiches et le listing
 * /dentiste/rabat est relégué en position 89 — la même requête est répondue
 * par 38 URLs du site. On garde la raison sociale, le praticien (« Dr X ») et,
 * selon la langue de la page, la graphie latine ou arabe.
 */

const CITY_RX =
  "(rabat|casablanca|casa|marrakech|tanger|f[eè]s|agadir|mekn[eè]s|oujda|k[eé]nitra|t[eé]touan|sal[eé]|t[eé]mara|mohammedia|el[- ]jadida|b[eé]ni[- ]mellal|nador|khouribga|settat|berrechid|safi|la[aâ]youne|essaouira|ouarzazate|taza|errachidia|guelmim|larache|khemisset|tiznit|inezgane|a[iï]t[- ]melloul|bouskoura|dar[- ]bouazza|skhirat|harhoura|agdal|maarif|ma[aâ]rif|gauthier|bourgogne|hay[- ]riad|souissi|racine|gu[eé]liz|ghandi|hay[- ]hassani|ain[- ]sebaa|sidi[- ]maarouf|californie|oasis|anfa|belv[eé]d[eè]re|roudani|ziraoui|zerktouni)";
const SPE_RX =
  "(chirurgien[- ]?dentiste|dentiste|orthodontiste|p[eé]dodontiste|implantologue|m[eé]decin(?: g[eé]n[eé]raliste| esth[eé]tique)?|g[eé]n[eé]raliste|kin[eé](?:sith[eé]rapeute|sith[eé]rapie)?|ost[eé]opathe|podologue|psychologue|psychiatre|dermatologue|gyn[eé]cologue|p[eé]diatre|cardiologue|ophtalmologue|orl|neurologue|urologue|rhumatologue|nutritionniste|di[eé]t[eé]ticien(?:ne)?|naturopathe|sexologue|angiologue|phl[eé]bologue|radiologue|pneumologue|endocrinologue|di[aé]b[eé]tologue|gastro(?:-?ent[eé]rologue)?|allergologue|n[eé]phrologue|oncologue|h[eé]matologue|orthophoniste|psychomotricien(?:ne)?|v[eé]t[eé]rinaire|opticien|orthoptiste|sage[- ]femme|acupuncteur|chiropracteur|hom[eé]opathe)s?";
/** Services énumérés par la source (« Implant dentaire I Facette dentaire »). */
const SERVICE_RX =
  "(implants?(?: dentaires?)?|facettes?(?: dentaires?)?|urgences?(?: dentaires?)?|hollywood smile|blanchiment(?: dentaire)?|invisalign|orthodontie|proth[eè]ses?(?: dentaires?)?|laser|[eé]pilation(?: laser)?(?: d[eé]finitive)?|botox|filler|m[eé]soth[eé]rapie|prp|skinbooster|d[eé]tatouage|amincissement|r[eé][eé]ducation|ventouse|cupping(?: therapy)?|acupuncture|posturologie|chiropraxie|greffe(?: de)? cheveux|soins?|esth[eé]tique|pluridisciplinaire|multidisciplinaire|urgences?)";

const STUFF_SEGMENT = new RegExp(
  `^(?:${SPE_RX}(?:\\s+(?:[àa]|de|en|-)?\\s*${CITY_RX})?|${CITY_RX}(?:\\s*[/-]\\s*${CITY_RX})?|(?:${SERVICE_RX}(?:\\s*(?:[,&/]|et|and)\\s*)?)+)\\s*$`,
  "iu",
);
const DR_SEGMENT = /^(?:dr\.?|pr\.?|professeur|docteur|الدكتور(?:ة)?|د\.)\s*\S/iu;
const ARABIC = /[؀-ۿ]/u;
const LATIN = /[A-Za-zÀ-ÿ]/u;

/** Découpe sur les séparateurs de la source : « - », « – », « — », « | », « I » et « l » isolés, « : », « / ». */
function splitSegments(raw: string): string[] {
  return raw
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, " ") // emoji (📍)
    .replace(/\s+[Il|]\s+/g, " | ") // « Facette dentaire I Dentiste Casablanca », « Idrissi l Dentiste »
    .replace(/\s+[-–—]+\s+|\s*[|/:]\s*|\s+_/g, " | ")
    .split("|")
    .map((s) => s.replace(/\s+/g, " ").trim())
    .filter((s) => s.length > 1);
}

function isStuffing(seg: string): boolean {
  return STUFF_SEGMENT.test(seg);
}

/**
 * Nettoie la raison sociale pour l'affichage : conserve le premier segment qui
 * est un vrai nom (ni « <spé> <ville> », ni liste de services), y ajoute le
 * praticien s'il apparaît dans un autre segment, et garde la graphie de la
 * langue de la page quand le nom est bilingue. Renvoie la chaîne d'origine
 * (nettoyée des espaces) quand aucune règle ne s'applique.
 */
export function cleanCenterDisplayName(raw: string, locale: string = "fr"): string {
  const original = raw.replace(/\s+/g, " ").trim();
  if (!original) return original;
  const segments = splitSegments(original);
  if (segments.length <= 1 && !ARABIC.test(original)) return original;

  const wantArabic = locale === "ar";
  const scriptOk = (s: string) => (wantArabic ? ARABIC.test(s) || !LATIN.test(s) : LATIN.test(s) || !ARABIC.test(s));
  // Un segment mixte (latin + arabe collés, sans séparateur) est scindé sur le
  // changement d'alphabet.
  const parts: string[] = [];
  for (const seg of segments) {
    const m = seg.match(/^([^؀-ۿ]*[A-Za-zÀ-ÿ0-9][^؀-ۿ]*?)\s+([؀-ۿ].*)$/u);
    if (m) parts.push(m[1]!.trim(), m[2]!.trim());
    else parts.push(seg);
  }
  const candidates = parts.filter((s) => s.length > 1 && !isStuffing(s));
  const names = candidates.filter((s) => !DR_SEGMENT.test(s) && scriptOk(s));
  const drs = candidates.filter((s) => DR_SEGMENT.test(s) && scriptOk(s));

  let name = stripTail(names[0] ?? drs[0] ?? candidates.find((s) => !DR_SEGMENT.test(s)) ?? candidates[0] ?? "");
  // « Centre Dentaire: Sweet Smile » → le premier segment n'est qu'un type de
  // structure : on lui accole le segment nominal suivant.
  if (GENERIC_ONLY.test(name)) {
    const next = names.find((s) => stripTail(s) !== name && !GENERIC_ONLY.test(stripTail(s)));
    if (next) name = `${name} ${stripTail(next)}`;
  }
  // Praticien cité dans un autre segment et absent du nom retenu → conservé,
  // débarrassé de sa queue « Dentiste Rabat ».
  const dr = drs.map(stripTail).find((d) => d && d !== name);
  if (dr) {
    const surname = dr.replace(/^(?:dr\.?|pr\.?|professeur|docteur|الدكتور(?:ة)?|د\.)\s*/iu, "").trim().split(/\s+/).pop() ?? "";
    if (surname.length >= 3 && !name.toLowerCase().includes(surname.toLowerCase())) name = `${name} – ${dr}`;
  }
  name = name.replace(/\s*[-–—,;:]+\s*$/u, "").trim();
  return name.length >= 3 ? name : original;
}

const GENERIC_ONLY = /^(?:centre|cabinet|clinique|polyclinique|espace|institut)(?:\s+(?:dentaire|m[eé]dicale?|de kin[eé]sith[eé]rapie|de radiologie|d'ophtalmologie|de sant[eé]))?\s*$/iu;
const TAIL_STUFF = new RegExp(`\\s*(?:\\(\\s*(?:${SPE_RX}|${CITY_RX}|${SERVICE_RX})[^)]*\\)|(?:${SPE_RX})(?:\\s+(?:[àa]\\s+)?${CITY_RX})?|${SERVICE_RX})\\s*$`, "iu");

/** Retire jusqu'à deux queues « Dentiste Rabat », « ( DENTISTE MEKNES) »,
 *  « Soins & Esthétique » d'un segment, tant qu'il reste un nom d'au moins
 *  deux mots (ou un mot non générique). */
function stripTail(seg: string): string {
  let s = seg.trim();
  for (let i = 0; i < 2; i++) {
    const next = s.replace(TAIL_STUFF, "").replace(/\s*[-–—,;:&]+\s*$/u, "").trim();
    if (next === s || next.length < 3 || GENERIC_ONLY.test(next)) break;
    s = next;
  }
  return s;
}
